import React from 'react';
import clsx from 'clsx';
import { useLanguage } from '../../i18n/useLanguage';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalElements,
  pageSize,
  onPageChange,
  onPageSizeChange,
  className,
}) => {
  const { t } = useLanguage();
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage < 3) {
        for (let i = 0; i < 4; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages - 1);
      } else if (currentPage > totalPages - 4) {
        pages.push(0);
        pages.push('ellipsis');
        for (let i = totalPages - 4; i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(0);
        pages.push('ellipsis');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages - 1);
      }
    }

    return pages;
  };

  const showPageControls = totalPages > 1;

  return (
    <div className={clsx('pagination', className)}>
      <div className="pagination-info">
        {t('pagination.showing')} {totalElements === 0 ? 0 : currentPage * pageSize + 1} {t('pagination.to')}{' '}
        {Math.min((currentPage + 1) * pageSize, totalElements)} {t('pagination.of')} {totalElements} {t('pagination.posts')}
      </div>

      {showPageControls && (
        <div className="pagination-controls">
          <button
            className="pagination-button"
            onClick={() => onPageChange(0)}
            disabled={currentPage === 0}
          >
            {t('pagination.first')}
          </button>
          <button
            className="pagination-button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {t('pagination.previous')}
          </button>

          {getPageNumbers().map((page, index) => {
            if (page === 'ellipsis') {
              return (
                <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                  ...
                </span>
              );
            }
            return (
              <button
                key={page}
                className={clsx(
                  'pagination-button',
                  'pagination-button--number',
                  currentPage === page && 'pagination-button--active'
                )}
                onClick={() => onPageChange(page as number)}
              >
                {(page as number) + 1}
              </button>
            );
          })}

          <button
            className="pagination-button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          >
            {t('pagination.next')}
          </button>
          <button
            className="pagination-button"
            onClick={() => onPageChange(totalPages - 1)}
            disabled={currentPage >= totalPages - 1}
          >
            {t('pagination.last')}
          </button>
        </div>
      )}

      {onPageSizeChange && (
        <div className="pagination-size">
          <label>
            {t('pagination.perPage')}
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </label>
        </div>
      )}
    </div>
  );
};

